import { INVOICE_TYPE, VOUCHERS_TYPES } from "constants/general";
import {
  ADMINS_SHOW,
  BANK_RECONCILIATIONS_INDEX,
  BOOKING_CONTRACT_VIEW,
  CONTRACTS_SHOW,
  CREDIT_NOTE_SHOW,
  CUSTOMERS_SHOW,
  DEALS_SHOW,
  DEBIT_NOTE_SHOW,
  DEFERRAL_SCHEDULES_INDEX,
  DOCUMENTS_INDEX,
  DOUBTFUL_DEBT_PROVISIONS_INDEX,
  EMPLOYEE_CUSTODIES_INDEX,
  EMPLOYEE_LOANS_INDEX,
  EMPLOYEES_SHOW,
  END_OF_SERVICES_INDEX,
  ESIGN_INDEX,
  FISCAL_YEAR_CLOSINGS_INDEX,
  FIXED_ASSETS_INDEX,
  INVENTORIES_SHOW,
  LEADS_SHOW,
  LETTERS_INDEX,
  MAINTENANCE_REQUESTS_INDEX,
  OPENING_BALANCES_INDEX,
  PAYROLL_RUNS_INDEX,
  PO_INDEX,
  PRICE_QUOTATIONS_SHOW,
  PROJECTS_SHOW,
  PROPERTIES_SHOW,
  PURCHASE_INVOICES_INDEX,
  RECEIVABLE_WRITEOFFS_INDEX,
  RECURRING_JOURNALS_INDEX,
  ROLES_SHOW,
  RPO_INDEX,
  SALES_CONTRACTS_SHOW,
  SALES_INVOICES_SHOW,
  SANADAT_SHOW,
  SERVICE_CONTRACTS_SHOW,
  SUPPORT_CENTER_TICKETS_SHOW,
  TASKS_SHOW,
  UNIT_MESSAGES_INDEX,
  UNITS_SHOW,
  VOUCHERS_DESTRUCTION_VIEW,
  VOUCHERS_RETURN_VIEW,
  VOUCHERS_SUPPLY_VIEW,
  VOUCHERS_TRANSFER_VIEW,
  VOUCHERS_WITHDRAW_VIEW,
  WORK_ORDERS_VIEW,
} from "permissions";
import { LANGUAGE } from "services/general";
import { HAS_PERMISSION } from "services/user";

/**
 * Every record the ERP opens on a page of its own.
 *
 * Values are the backend morph aliases (`MorphMapServiceProvider`) wherever one exists, so a
 * polymorphic `*_type` column off an API row (`payable_type`, `voucherable_type`, …) can be
 * handed to `<RecordLink type>` as it is. Invoices share one alias across several pages, so
 * they are keyed by page and resolved from the row's `type` by `invoiceRecordType()`.
 */
export const RECORD_TYPE = {
  CUSTOMER: "customer",
  EMPLOYEE: "employee",
  EMPLOYEE_CUSTODY: "employee_custody",
  EMPLOYEE_LOAN: "employee_loan",
  PAYROLL_RUN: "payroll_run",
  END_OF_SERVICE: "employee_end_of_service",
  UNIT: "unit",
  PROPERTY: "unit_property",
  PROJECT: "unit_project",
  UNIT_CONVERSATION: "unit_conversation",
  RENT_CONTRACT: "rent_contract",
  SELL_CONTRACT: "sell_contract",
  BOOKING_CONTRACT: "booking_contract",
  SERVICE_CONTRACT: "unit_service_assign",
  SALES_INVOICE: "sales_invoice",
  CREDIT_NOTE: "credit_note",
  PURCHASE_INVOICE: "purchase_invoice",
  DEBIT_NOTE: "debit_note",
  PURCHASE_REQUEST: "rpo",
  PURCHASE_ORDER: "po",
  PRICE_QUOTATION: "price_quotation",
  VOUCHER: "voucher",
  INVENTORY_VOUCHER: "inventory_voucher",
  WITHDRAW_VOUCHER: "inventory_voucher_withdraw",
  SUPPLY_VOUCHER: "inventory_voucher_supply",
  RETURN_VOUCHER: "inventory_voucher_return",
  TRANSFER_VOUCHER: "inventory_voucher_transfer",
  DESTRUCTION_VOUCHER: "inventory_voucher_destruction",
  PRODUCT: "inventory_product",
  MAINTENANCE_REQUEST: "maintenance_request",
  WORK_ORDER: "maintenance_work_order",
  LEAD: "crm_lead",
  DEAL: "crm_deal",
  FIXED_ASSET: "fixed_asset",
  DEPRECIATION_RUN: "fixed_asset_depreciation_run",
  OPENING_BALANCE: "opening_balance_import",
  RECURRING_JOURNAL: "recurring_journal",
  DEFERRAL_SCHEDULE: "deferral_schedule",
  BANK_RECONCILIATION: "bank_reconciliation",
  FISCAL_YEAR_CLOSING: "fiscal_year_closing",
  DOUBTFUL_DEBT_PROVISION: "doubtful_debt_provision",
  RECEIVABLE_WRITEOFF: "receivable_writeoff",
  DOCUMENT: "document",
  LETTER: "letter",
  ESIGN_REQUEST: "esign_request",
  TASK: "agent_task",
  SUPPORT_TICKET: "agent_support_ticket",
  // The platform's own support (served from the central API), not the tenant's support center.
  AMER_SUPPORT_TICKET: "amer_support_ticket",
  ROLE: "role",
  // A staff account (`users`), opened on its admin profile page.
  ADMIN: "user",
};

/**
 * `url` mirrors the record's detail route in `components/routes/routes.js`; `permission` is
 * that route's `permissionId`, so a user who would be bounced to "access denied" is shown
 * plain text instead of a link. `null` means the route carries no gate.
 */
const RECORD_LINKS = {
  [RECORD_TYPE.CUSTOMER]: {
    url: (id) => `/${LANGUAGE}/customers/${id}`,
    permission: CUSTOMERS_SHOW,
    // `CustomerController::show` reads withTrashed(), so a deleted customer's page still opens.
    opensWhenDeleted: true,
  },
  [RECORD_TYPE.EMPLOYEE]: {
    url: (id) => `/${LANGUAGE}/employees/${id}`,
    permission: EMPLOYEES_SHOW,
  },
  [RECORD_TYPE.EMPLOYEE_CUSTODY]: {
    url: (id) => `/${LANGUAGE}/employee-custodies/${id}`,
    permission: EMPLOYEE_CUSTODIES_INDEX,
  },
  [RECORD_TYPE.EMPLOYEE_LOAN]: {
    url: (id) => `/${LANGUAGE}/employee-loans/${id}`,
    permission: EMPLOYEE_LOANS_INDEX,
  },
  [RECORD_TYPE.PAYROLL_RUN]: {
    url: (id) => `/${LANGUAGE}/payroll-runs/${id}`,
    permission: PAYROLL_RUNS_INDEX,
  },
  [RECORD_TYPE.END_OF_SERVICE]: {
    url: (id) => `/${LANGUAGE}/end-of-services/${id}`,
    permission: END_OF_SERVICES_INDEX,
  },
  [RECORD_TYPE.UNIT]: {
    url: (id) => `/${LANGUAGE}/units/${id}`,
    permission: UNITS_SHOW,
  },
  [RECORD_TYPE.PROPERTY]: {
    url: (id) => `/${LANGUAGE}/properties/${id}`,
    permission: PROPERTIES_SHOW,
  },
  [RECORD_TYPE.PROJECT]: {
    url: (id) => `/${LANGUAGE}/projects/${id}`,
    permission: PROJECTS_SHOW,
  },
  [RECORD_TYPE.UNIT_CONVERSATION]: {
    url: (id) => `/${LANGUAGE}/units-messages/${id}`,
    permission: UNIT_MESSAGES_INDEX,
  },
  [RECORD_TYPE.RENT_CONTRACT]: {
    url: (id) => `/${LANGUAGE}/rent-contracts/${id}`,
    permission: CONTRACTS_SHOW,
  },
  [RECORD_TYPE.SELL_CONTRACT]: {
    url: (id) => `/${LANGUAGE}/sales-contracts/${id}`,
    permission: SALES_CONTRACTS_SHOW,
  },
  [RECORD_TYPE.BOOKING_CONTRACT]: {
    url: (id) => `/${LANGUAGE}/booking-contracts/${id}`,
    permission: BOOKING_CONTRACT_VIEW,
  },
  [RECORD_TYPE.SERVICE_CONTRACT]: {
    url: (id) => `/${LANGUAGE}/service-contracts/${id}`,
    permission: SERVICE_CONTRACTS_SHOW,
  },
  [RECORD_TYPE.SALES_INVOICE]: {
    url: (id) => `/${LANGUAGE}/sales-invoices/${id}`,
    permission: SALES_INVOICES_SHOW,
  },
  [RECORD_TYPE.CREDIT_NOTE]: {
    url: (id) => `/${LANGUAGE}/credit-notes/${id}`,
    permission: CREDIT_NOTE_SHOW,
  },
  [RECORD_TYPE.PURCHASE_INVOICE]: {
    url: (id) => `/${LANGUAGE}/purchase-invoices/${id}`,
    permission: PURCHASE_INVOICES_INDEX,
  },
  [RECORD_TYPE.DEBIT_NOTE]: {
    url: (id) => `/${LANGUAGE}/debit-notes/${id}`,
    permission: DEBIT_NOTE_SHOW,
  },
  [RECORD_TYPE.PURCHASE_REQUEST]: {
    url: (id) => `/${LANGUAGE}/rpos/${id}`,
    permission: RPO_INDEX,
  },
  [RECORD_TYPE.PURCHASE_ORDER]: {
    url: (id) => `/${LANGUAGE}/purchase-orders/${id}`,
    permission: PO_INDEX,
  },
  [RECORD_TYPE.PRICE_QUOTATION]: {
    url: (id) => `/${LANGUAGE}/price-quotations/${id}`,
    permission: PRICE_QUOTATIONS_SHOW,
  },
  [RECORD_TYPE.VOUCHER]: {
    url: (id) => `/${LANGUAGE}/vouchers/${id}`,
    permission: SANADAT_SHOW,
  },
  // A voucher whose type the caller does not know. Prefer the typed entries below: their URL
  // sits under that type's list, so the sidebar keeps the list's group open and its leaf lit.
  [RECORD_TYPE.INVENTORY_VOUCHER]: {
    url: (id) => `/${LANGUAGE}/voucher/${id}`,
    permission: null,
  },
  [RECORD_TYPE.WITHDRAW_VOUCHER]: {
    url: (id) => `/${LANGUAGE}/inventory-vouchers/withdraw/${id}`,
    permission: VOUCHERS_WITHDRAW_VIEW,
  },
  [RECORD_TYPE.SUPPLY_VOUCHER]: {
    url: (id) => `/${LANGUAGE}/inventory-vouchers/supply/${id}`,
    permission: VOUCHERS_SUPPLY_VIEW,
  },
  [RECORD_TYPE.RETURN_VOUCHER]: {
    url: (id) => `/${LANGUAGE}/inventory-vouchers/return/${id}`,
    permission: VOUCHERS_RETURN_VIEW,
  },
  [RECORD_TYPE.TRANSFER_VOUCHER]: {
    url: (id) => `/${LANGUAGE}/inventory-vouchers/transfer/${id}`,
    permission: VOUCHERS_TRANSFER_VIEW,
  },
  [RECORD_TYPE.DESTRUCTION_VOUCHER]: {
    url: (id) => `/${LANGUAGE}/inventory-vouchers/destruction/${id}`,
    permission: VOUCHERS_DESTRUCTION_VIEW,
  },
  [RECORD_TYPE.PRODUCT]: {
    url: (id) => `/${LANGUAGE}/products/${id}`,
    permission: INVENTORIES_SHOW,
  },
  [RECORD_TYPE.MAINTENANCE_REQUEST]: {
    url: (id) => `/${LANGUAGE}/maintenance/requests/${id}`,
    permission: MAINTENANCE_REQUESTS_INDEX,
  },
  [RECORD_TYPE.WORK_ORDER]: {
    url: (id) => `/${LANGUAGE}/maintenance/work-orders/${id}`,
    permission: WORK_ORDERS_VIEW,
  },
  [RECORD_TYPE.LEAD]: {
    url: (id) => `/${LANGUAGE}/crm/potential-customers/${id}`,
    permission: LEADS_SHOW,
  },
  [RECORD_TYPE.DEAL]: {
    url: (id) => `/${LANGUAGE}/crm/deals/${id}`,
    permission: DEALS_SHOW,
  },
  [RECORD_TYPE.FIXED_ASSET]: {
    url: (id) => `/${LANGUAGE}/fixed-assets/${id}`,
    permission: FIXED_ASSETS_INDEX,
  },
  [RECORD_TYPE.DEPRECIATION_RUN]: {
    url: (id) => `/${LANGUAGE}/fixed-assets/depreciation-runs/${id}`,
    permission: FIXED_ASSETS_INDEX,
  },
  [RECORD_TYPE.OPENING_BALANCE]: {
    url: (id) => `/${LANGUAGE}/opening-balances/${id}`,
    permission: OPENING_BALANCES_INDEX,
  },
  [RECORD_TYPE.RECURRING_JOURNAL]: {
    url: (id) => `/${LANGUAGE}/recurring-journals/${id}`,
    permission: RECURRING_JOURNALS_INDEX,
  },
  [RECORD_TYPE.DEFERRAL_SCHEDULE]: {
    url: (id) => `/${LANGUAGE}/deferral-schedules/${id}`,
    permission: DEFERRAL_SCHEDULES_INDEX,
  },
  [RECORD_TYPE.BANK_RECONCILIATION]: {
    url: (id) => `/${LANGUAGE}/bank-reconciliations/${id}`,
    permission: BANK_RECONCILIATIONS_INDEX,
  },
  [RECORD_TYPE.FISCAL_YEAR_CLOSING]: {
    url: (id) => `/${LANGUAGE}/fiscal-year-closings/${id}`,
    permission: FISCAL_YEAR_CLOSINGS_INDEX,
  },
  [RECORD_TYPE.DOUBTFUL_DEBT_PROVISION]: {
    url: (id) => `/${LANGUAGE}/doubtful-debt-provisions/${id}`,
    permission: DOUBTFUL_DEBT_PROVISIONS_INDEX,
  },
  [RECORD_TYPE.RECEIVABLE_WRITEOFF]: {
    url: (id) => `/${LANGUAGE}/receivable-writeoffs/${id}`,
    permission: RECEIVABLE_WRITEOFFS_INDEX,
  },
  [RECORD_TYPE.DOCUMENT]: {
    url: (id) => `/${LANGUAGE}/documents/${id}`,
    permission: DOCUMENTS_INDEX,
  },
  [RECORD_TYPE.LETTER]: {
    url: (id) => `/${LANGUAGE}/letters/${id}`,
    permission: LETTERS_INDEX,
  },
  [RECORD_TYPE.ESIGN_REQUEST]: {
    url: (id) => `/${LANGUAGE}/esign-requests/${id}`,
    permission: ESIGN_INDEX,
    // `EsignRequestRepository::findWithDetails` reads withTrashed().
    opensWhenDeleted: true,
  },
  [RECORD_TYPE.TASK]: {
    url: (id) => `/${LANGUAGE}/task/${id}`,
    permission: TASKS_SHOW,
  },
  [RECORD_TYPE.SUPPORT_TICKET]: {
    url: (id) => `/${LANGUAGE}/support-details/${id}`,
    permission: SUPPORT_CENTER_TICKETS_SHOW,
  },
  [RECORD_TYPE.AMER_SUPPORT_TICKET]: {
    url: (id) => `/${LANGUAGE}/amer-support/${id}`,
    // Open to every signed-in user: the route skips the permission gate.
    permission: null,
  },
  [RECORD_TYPE.ROLE]: {
    url: (id) => `/${LANGUAGE}/admin/roles/${id}/view`,
    permission: ROLES_SHOW,
  },
  [RECORD_TYPE.ADMIN]: {
    url: (id) => `/${LANGUAGE}/admins/${id}`,
    permission: ADMINS_SHOW,
  },
};

/**
 * Which invoice page an invoice-like row opens, from its `type`. For a row that IS an invoice;
 * an unrecognised (or missing) type opens the sales invoice, so a row whose enum the frontend
 * has not caught up with still goes somewhere. A polymorphic reference that only NAMES an invoice
 * goes through `refModelRecordType`, which does not guess.
 */
const INVOICE_RECORD_TYPE = {
  [INVOICE_TYPE.SALES]: RECORD_TYPE.SALES_INVOICE,
  [INVOICE_TYPE.S_REFUND]: RECORD_TYPE.SALES_INVOICE,
  [INVOICE_TYPE.CREDIT_NOTE]: RECORD_TYPE.CREDIT_NOTE,
  [INVOICE_TYPE.PURCHASE]: RECORD_TYPE.PURCHASE_INVOICE,
  [INVOICE_TYPE.P_REFUND]: RECORD_TYPE.PURCHASE_INVOICE,
  [INVOICE_TYPE.DEBIT_NOTE]: RECORD_TYPE.DEBIT_NOTE,
  [INVOICE_TYPE.RPO]: RECORD_TYPE.PURCHASE_REQUEST,
  [INVOICE_TYPE.PO]: RECORD_TYPE.PURCHASE_ORDER,
};

export const invoiceRecordType = (invoiceType) =>
  INVOICE_RECORD_TYPE[invoiceType] ?? RECORD_TYPE.SALES_INVOICE;

/** `recordType` for a table whose rows are invoices of mixed kinds; a stable reference, so it keeps `DataTable`'s memo. */
export const invoiceRowRecordType = (row) => invoiceRecordType(row?.type);

/** The page of an invoice-like row (`{ id, type }`), for a row action's `to`. */
export const invoiceUrl = (invoice) =>
  recordUrl(invoiceRecordType(invoice?.type), invoice?.id);

/**
 * Inventory vouchers share one morph alias, so the voucher's own `type` (withdraw, supply, …)
 * picks its page. An absent or unknown type falls back to the untyped `voucher/:id` page.
 */
const INVENTORY_VOUCHER_RECORD_TYPE = {
  [VOUCHERS_TYPES.WITHDRAW]: RECORD_TYPE.WITHDRAW_VOUCHER,
  [VOUCHERS_TYPES.SUPPLY]: RECORD_TYPE.SUPPLY_VOUCHER,
  [VOUCHERS_TYPES.RETURN]: RECORD_TYPE.RETURN_VOUCHER,
  [VOUCHERS_TYPES.TRANSFER]: RECORD_TYPE.TRANSFER_VOUCHER,
  [VOUCHERS_TYPES.DESTRUCTION]: RECORD_TYPE.DESTRUCTION_VOUCHER,
};

export const inventoryVoucherRecordType = (voucherType) =>
  INVENTORY_VOUCHER_RECORD_TYPE[voucherType] ?? RECORD_TYPE.INVENTORY_VOUCHER;

// The morph alias of every invoice; the invoice's own `type` picks its page.
const INVOICE_REF_MODEL = "invoice";

/**
 * The page a polymorphic reference opens: a `ref_model` alias plus, for an invoice, the invoice's
 * own `type`. An invoice reference whose type is unknown has no page to name — it stays text
 * rather than guessing the sales invoice. Any other alias is returned as is (an alias with no
 * page also stays text).
 */
export const refModelRecordType = (refModel, invoiceType) => {
  if (refModel !== INVOICE_REF_MODEL) return refModel;
  return invoiceType ? invoiceRecordType(invoiceType) : undefined;
};

/** The record's page, or `null` when the type has none or the row came without an id. */
export const recordUrl = (type, id) => {
  if (id == null || id === "") return null;
  return RECORD_LINKS[type]?.url(id) ?? null;
};

/** The permission id the record's page is gated on; `null` when the route carries no gate. */
export const recordPermission = (type) =>
  RECORD_LINKS[type]?.permission ?? null;

/** Whether the page still opens for a soft-deleted record (a customer's does; most 404). */
export const opensWhenDeleted = (type) =>
  Boolean(RECORD_LINKS[type]?.opensWhenDeleted);

/** Whether the signed-in user may open this kind of record's page. */
export const canOpenRecord = (type) => {
  const link = RECORD_LINKS[type];
  if (!link) return false;
  return link.permission == null || Boolean(HAS_PERMISSION(link.permission));
};
